/**
 * Quotation Service
 * Handles API requests related to quotations (core operations)
 */
import { createService } from './serviceFactory';

// Define the base URL for quotation service
const baseUrl = '/api/supplier/quotation';

// Create base service
const baseService = createService(baseUrl);

const quotationService = {
  /**
   * Search quotations with filters
   * @param {Object} searchParams - Search parameters including page, pageSize, etc.
   * @returns {Promise} - Response with quotation items
   */
  searchQuotations(searchParams = {}) {
    // Set default values for pagination if not provided
    const params = {
      page: searchParams.page || 1, // Start at page 1 for UI (converts to 0 for API)
      pageSize: searchParams.pageSize || 10,
      searchText: searchParams.searchText || '',
      sorting: searchParams.sorting || '',
    };
    
    // Handle trackingStatus as a number or array
    if (searchParams.trackingStatus !== undefined) {
      params.trackingStatus = searchParams.trackingStatus;
    }
    
    return baseService.search(params);
  },
  
  /**
   * Get a single quotation by ID
   * @param {string} id - Quotation ID
   * @returns {Promise} - Response with quotation details
   */
  getQuotation(id) {
    return baseService.getById(id);
  },
  
  /**
   * Submit a quotation response
   * @param {string} id - Quotation ID
   * @param {Object} data - Quotation data
   * @returns {Promise} - Response
   */
  submitQuotation(id, data) {
    return baseService.update(id, data);
  },
  
  /**
   * Delete a quotation
   * @param {string} id - Quotation ID
   * @returns {Promise} - Response
   */
  deleteQuotation(id) {
    return baseService.delete(id);
  },

  /**
   * Decline a quotation
   * @param {string} quotationId - Quotation ID
   * @param {string} declineReason - Reason for declining the quotation
   * @returns {Promise} - Response
   */
  declineQuotation(quotationId, declineReason = '') {
    // According to swagger, the URL should be: /api/supplier/quotation/decline-quotation/{quotationId}
    const payload = declineReason ? { declineReason } : {};
    return baseService.client.post(`${baseUrl}/decline-quotation/${quotationId}`, payload).then(response => response.data);
  },

  /**
   * Send quotation to Nozzle
   * @param {string} quotationId - Quotation ID
   * @returns {Promise} - Response
   */
  sendToNozzleQuotation(quotationId) {
    return baseService.client.post(`${baseUrl}/send-to-nozzle-quotation/${quotationId}`).then(response => response.data);
  },

  /**
   * Check if quotation has any item with unit price
   * Backend: GET /api/supplier/quotation/has-any-item-with-unit-price/{quotationId}
   * @param {string} quotationId
   * @returns {Promise<boolean>}
   */
  hasAnyItemWithUnitPrice(quotationId) {
    return baseService.client
      .get(`${baseUrl}/has-any-item-with-unit-price/${quotationId}`)
      .then((response) => response.data === true);
  },

  // For backward compatibility - methods that are now in other services
  // These methods delegate to the appropriate service

  // Delegate to quotationItemService
  getQuotationItems(quotationId, params) {
    return import('./quotationItemService').then(module => {
      return module.default.getQuotationItems(quotationId, params);
    });
  },

  updateQuotationItems(quotationId, items) {
    return import('./quotationItemService').then(module => {
      return module.default.updateQuotationItems(quotationId, items);
    });
  },
  
  calculateItemPrice(quotationItemId, unitPrice) {
    return import('./quotationItemService').then(module => {
      return module.default.calculateItemPrice(quotationItemId, unitPrice);
    });
  },
  
  // Delegate to quotationConditionService
  getConditionCategories(quotationId) {
    return import('./quotationConditionService').then(module => {
      return module.default.getConditionCategories(quotationId);
    });
  },
  
  updateAdditionalCosts(quotationId, data) {
    return import('./quotationConditionService').then(module => {
      return module.default.updateAdditionalCosts(quotationId, data);
    });
  },
  
  saveQuotationConditions(quotationId, data) {
    return import('./quotationConditionService').then(module => {
      return module.default.saveQuotationConditions(quotationId, data);
    });
  },
  
  getQuotationConditions(quotationId) {
    return import('./quotationConditionService').then(module => {
      return module.default.getQuotationConditions(quotationId);
    });
  },
  
  deleteCondition(conditionId) {
    return import('./quotationConditionService').then(module => {
      return module.default.deleteCondition(conditionId);
    });
  },
  
  // Delegate to auditLogService
  getAuditLogs(quotationId) {
    return import('./auditLogService').then(module => {
      return module.default.getAuditLogs(quotationId);
    });
  },
  
  // Delegate to referenceDataService
  getRequestedItemTypes(quotationId) {
    return import('./referenceDataService').then(module => {
      return module.default.getRequestedItemTypes(quotationId);
    });
  },
  
  getCurrencies(quotationId) {
    return import('./referenceDataService').then(module => {
      return module.default.getCurrencies(quotationId);
    });
  },

  getConditionById(conditionId) {
    return import('./quotationConditionService').then(module => {
      return module.default.getConditionById(conditionId);
    });
  },
};

export default quotationService; 